import React, { useContext, useEffect, useRef, useState } from 'react';
import FileUpload from './FileUpload/FileUpload';
import ViewerContext from '../context/ViewerContext';
import { setCamera2 } from '../helper/cesiumFunctions';
import { getDataSource, zoomToEntityBound } from '../helper/cesiumHelper';
import { EventHelper } from 'cesium';
import './toolBar.scss';
import { createSvgIcon } from '../helper/czmlHelper';
import { getReplayModes, CHASE_OFFSET } from '../helper/replayModes';
import { getVarioTrack } from '../helper/varioTrack';
import {
    isFlightHidden,
    setFlightHidden,
    onFlightVisibilityChange,
} from '../helper/flightVisibility';
import { getSoloFlight, setSoloFlight, onSoloChange } from '../helper/soloMode';
const ToolbarComponent = () => {

    const {
        igcParsedData, coloredFile, setColoredFile
    } = useContext(ViewerContext);
    const [update, setUpdate] = useState();
    const [trackedId, setTrackedId] = useState();
    const [cinemaId, setCinemaId] = useState();
    const [soloId, setSoloId] = useState(getSoloFlight());

    useEffect(() => {
        dataSourceEventHandling();

        const rm = getReplayModes(viewer);
        // Row highlight follows the logically-tracked pilot, in every mode
        // (even when the camera is tracking the invisible smooth proxy).
        // Cinema always films whoever is followed, so it lights up the same row.
        const offFollow = rm.onChange((s) => {
            setTrackedId(s.followedId);
            setCinemaId(s.cinema ? s.followedId : null);
        });
        setTrackedId(rm.followedId);
        setCinemaId(rm.cinemaOn ? rm.followedId : null);

        // Keep the chase offset for classic (raw) follow; never touch the proxy.
        const removeVF = viewer.trackedEntityChanged.addEventListener(function (trackedEntity) {
            if (trackedEntity && trackedEntity !== rm.proxy) {
                trackedEntity.viewFrom = CHASE_OFFSET;
            }
        });

        const offVisible = onFlightVisibilityChange(() => setUpdate((u) => !u));
        const offSolo = onSoloChange((id) => setSoloId(id));

        return () => { offFollow(); removeVF(); offVisible(); offSolo(); };
    }, []);

    const showHideFile = (fileId, status) => {
        // Source of truth: a flight has two possible data sources (<id> and
        // <id>_colored) and colouring destroys one and builds the other, so the
        // `show` flag alone doesn't survive (helper/flightVisibility.js).
        setFlightHidden(fileId, !status)

        const dataSource = getDataSourceById(fileId)
        if (dataSource) {
            dataSource.show = status
        }
        // The vario track is a separate primitive — hide it too when this is the
        // colored flight, so unchecking hides the whole flight, not just the label.
        const vt = getVarioTrack(viewer)
        if (vt.flightId === fileId) vt.setVisible(status)

        // Keep the camera on something that actually exists on screen.
        const rm = getReplayModes(viewer)
        let cameraTarget = null // the flight the camera ends up on, if it moved
        if (!status) {
            // Hidden the followed flight → hop to another still-visible one, or
            // release the follow if none remain.
            if (rm.followedId === fileId) {
                const nextEntity = findNextVisibleEntity(fileId)
                if (nextEntity) {
                    rm.followPilot(nextEntity)
                    cameraTarget = nextEntity.id
                } else {
                    rm.unfollow()
                }
            }
        } else if (!isFollowTargetVisible(rm)) {
            // Shown a flight while the camera is following nothing on screen →
            // it takes the camera. Without this, hiding the last visible pilot
            // BEFORE showing the next one leaves the camera parked on empty sky.
            const entity = getPilotEntity(fileId)
            if (entity) {
                rm.followPilot(entity)
                cameraTarget = entity.id
            }
        }

        // The Vario Track can't live on a hidden flight either — hand it to
        // whoever the camera is on now, so stepping from one single pilot to the
        // next brings the coloured track along instead of leaving it behind on
        // an unchecked row.
        const target = cameraTarget || (isFollowTargetVisible(rm) ? rm.followedId : null)
        if (coloredFile && isFlightHidden(coloredFile) && target && target !== coloredFile) {
            setColoredFile(target)
        }

        // Solo is camera + Vario Track + dimmed others on ONE pilot, so it moves
        // with them — and ends if nothing visible is left to solo.
        const solo = getSoloFlight()
        if (solo && isFlightHidden(solo)) {
            setSoloFlight(viewer, target && target !== solo ? target : null)
        }
    }

    // Is the camera following a pilot that is actually on screen? False when
    // nothing is followed, when the followed flight is unchecked, and when its
    // data source is gone (e.g. unloaded while the camera still held the id).
    const isFollowTargetVisible = (rm) => {
        const id = rm.followedId
        if (!id || isFlightHidden(id)) return false
        const ds = getDataSourceById(id)
        return !!ds && ds.show
    }

    // First still-visible flight other than excludeId (for camera hand-off).
    const findNextVisibleEntity = (excludeId) => {
        for (const f of (igcParsedData || [])) {
            if (f.id === excludeId) continue
            if (isFlightHidden(f.id)) continue
            const ds = getDataSourceById(f.id)
            if (ds && ds.show) {
                const entity =
                    ds.entities.getById?.(f.id) ||
                    ds.entities.values.find((e) => e.position)
                if (entity) return entity
            }
        }
        return null
    }

    const zoomTo = (fileId) => {
        if (isFlightHidden(fileId)) return // nothing on screen to fly to
        const dataSource = getDataSourceById(fileId)
        if (dataSource) {
            let entities = dataSource.entities.values
            if (entities.length > 0) {
                const entity = entities[0]
                zoomToEntityBound(entity);
            }
        }
    }

    const colorizeFunction = (fileId) => {

        if (fileId == coloredFile)
            setColoredFile(null)
        else
            setColoredFile(fileId);

        setUpdate(!update);
    }

    const zoomToOld = (index) => {
        var IniLat = CoordsPosTim[index][0][0] * 1;
        var IniLon = CoordsPosTim[index][0][1] * 1;
        var IniAlt = CoordsPosTim[index][0][2] * 1;

        var DistLat = IniLat - (IniLat * 0.0006);
        var DistLon = IniLon - (IniLon * 0);
        let height = IniAlt * 2.5;

        var duration = 3.0;
        setCamera2(DistLon, DistLat, height, duration);
    }

    const getRgbColor = (rgbArray) => {
        return `rgb(${rgbArray.join(',')})`
    }

    const getDisableStatus = (fileId) => {
        return !isFlightHidden(fileId);
    }

    const dataSourceEventHandling = () => {
        const cesiumEventHelper = new EventHelper()

        const cesiumDataSourceListener = (dataSource) => {
            setUpdate(!update)
        }

        cesiumEventHelper.add(viewer.dataSources.dataSourceAdded, cesiumDataSourceListener, cesiumEventHelper)
        cesiumEventHelper.add(viewer.dataSources.dataSourceRemoved, cesiumDataSourceListener, cesiumEventHelper)

    }

    const getPilotEntity = (id) => {
        const dataSource = getDataSourceById(id);
        return (
            dataSource?.entities.getById?.(id) ||
            dataSource?.entities.values.find((e) => e.position)
        );
    }

    const trackEntityFunction = (id) => {
        if (isFlightHidden(id)) return // the camera never follows a hidden track
        const rm = getReplayModes(viewer);
        if (rm.followedId == id) {
            rm.unfollow();
        } else {
            const entity = getPilotEntity(id);
            if (entity) rm.followPilot(entity);
        }
    }

    // Cinema: follow this pilot AND let the camera pick its own angle/altitude
    // from the track (close+steep in thermals, far back+flat on glides).
    const cinemaFunction = (id) => {
        if (isFlightHidden(id)) return // nothing on screen to film
        const rm = getReplayModes(viewer);
        if (rm.cinemaOn && rm.followedId == id) {
            rm.setCinema(false);
            return;
        }
        const entity = getPilotEntity(id);
        if (entity) rm.setCinema(true, entity);
    }
    // Solo: debrief one pilot while the group stays on screen, faded. Gives them
    // the Vario Track and the camera and dims everyone else (helper/soloMode.js).
    const soloFunction = (id) => {
        if (isFlightHidden(id)) return // nothing on screen to solo
        if (getSoloFlight() === id) {
            // Off: the others come back to full strength. The Vario Track and
            // the camera stay where they are — same as switching cinema off.
            setSoloFlight(viewer, null)
            return
        }
        setSoloFlight(viewer, id)
        setColoredFile(id)
        // Solo shows the Vario Track *immediately*. setColoredFile alone isn't
        // enough: when this pilot is already the coloured one React re-renders
        // nothing, so no data source is rebuilt and a track left hidden by an
        // earlier show/hide would stay hidden.
        const vt = getVarioTrack(viewer)
        if (vt.flightId === id) vt.setVisible(true)
        const entity = getPilotEntity(id)
        if (entity) getReplayModes(viewer).followPilot(entity)
    }

    const getDataSourceById = (fileId) => {
        return getDataSource(fileId, coloredFile, igcParsedData)
    }

    const updatePilotName =(id)=>{
        const dataSource = getDataSourceById(id)

        const newSVGContent = createSvgIcon("Updated Name", [255, 0, 0])
        const entity = viewer.selectedEntity
        // const base64String = entity.billboard.image.getValue();
        // const svgContentBase64 = base64String.split(',')[1];
        // const decodedSVGContent = atob(svgContentBase64);
        // let newSVGContent = decodedSVGContent.replace("Varga Abel", "Updated Name");
        entity.billboard.image.setValue(newSVGContent);

    }

    return (
        <div className="toolbar-container">
            <table style={{ display: "block", width: "100%" }}>
                <tbody>
                    <tr>
                        <td style={{ width: "25px", display: "none" }} className="ImgWait">
                            <img src="./loader.gif" height="25" alt="" border="0" className="ImgWait" style={{ display: "none" }} />

                        </td>
                    </tr>
                    {igcParsedData?.map((item, index) => (
                        <tr key={index}>
                            <td style={{ width: '20px' }}>
                                <input
                                    type="checkbox"
                                    checked={!isFlightHidden(item.id)}
                                    onChange={(e) => showHideFile(item.id, e.target.checked)}
                                />
                            </td>
                            <td style={{ width: '20px' }}>
                                <div style={{ backgroundColor: getRgbColor(item.color), padding: '5px' }}></div>
                            </td>
                            <td>
                                <span
                                    className={"zoom" + (isFlightHidden(item.id) ? " disabled" : "")}
                                    onClick={() => zoomTo(item.id)}
                                >
                                    {item.pilotName}
                                </span>
                            </td>
                            {(igcParsedData && igcParsedData.length > 1) && <td>
                                <span
                                    className={"zoom" + (item.id == coloredFile ? " selected" : "") + (getDisableStatus(item.id) ? " " : " disabled")}
                                    onClick={() => colorizeFunction(item.id)}
                                >Vario Track</span>

                            </td>}
                            {/* All three icons in ONE cell: a cell boundary between
                                them costs more width than their margins do. */}
                            <td className="icon-cell">
                                {(igcParsedData && igcParsedData.length > 1) &&
                                    <span className={'icon-button' + (isFlightHidden(item.id) ? ' disabled' : '')}><img className={(soloId != item.id) ? 'solo-off' : ''}
                                        onClick={() => soloFunction(item.id)}
                                        src="./icons/solo.svg" alt="Solo" title="Solo: Vario Track + camera on this pilot, everyone else dimmed" />
                                    </span>}
                                <span className={'icon-button' + (isFlightHidden(item.id) ? ' disabled' : '')}><img className={(trackedId != item.id) ? 'camera-unfollow' : ''}
                                    onClick={() => trackEntityFunction(item.id)}
                                    src="./icons/camera.svg" alt="Un Track Entity" title="Follow this pilot" />
                                </span>
                                <span className={'icon-button' + (isFlightHidden(item.id) ? ' disabled' : '')}><img className={(cinemaId != item.id) ? 'cinema-off' : ''}
                                    onClick={() => cinemaFunction(item.id)}
                                    src="./icons/cinema.svg" alt="Cinema Camera" title="Cinema camera" />
                                </span>
                            </td>
                            {/* <td>
                                <span className='zoom'><img
                                    onClick={() => updatePilotName(item.id)}
                                    src="./icons/edit.svg" alt="Edit Pilot" />
                                </span>
                            </td> */}
                        </tr>
                    ))}
                    <FileUpload />
                    
                </tbody>
            </table>
        </div>
    );
};

export default ToolbarComponent;
